import React from 'react'
import { SpreadsheetReducerActions, ISpreadsheetData } from 'components/spreadsheet/types'
import { HEADER_HEIGHT_CLASS, COLUMN_MIN_WIDTH_CLASS } from 'style/constants'
import { Plus } from 'components/icons'
import Tooltip from 'components/tooltip'
import { useColumnTypePicker } from 'components/spreadsheet/components/column-type-picker/useColumnTypePicker'
import ColumnTypePickerMenu from 'components/spreadsheet/components/column-type-picker'

interface HeaderProps {
  spreadsheetData: ISpreadsheetData
  setSpreadsheetData: React.Dispatch<SpreadsheetReducerActions>
  totalWidth: number
}

const HeaderAddColumn: React.FC<HeaderProps> = ({ spreadsheetData, setSpreadsheetData, totalWidth }) => {
  const picker = useColumnTypePicker(spreadsheetData, setSpreadsheetData)

  // Clicking + opens the type picker over where the new column will go. Picking a type adds the column
  // instantly with defaults; the per-row configure affordance opens a second stage to set options
  // first. Nothing is created until a type is picked or the configure stage is submitted.
  const handleAddColumn = (event: React.MouseEvent): void => {
    const rect = event.currentTarget.getBoundingClientRect()
    const maxSortOrder = Math.max(...spreadsheetData.viewDetails.columns.map((col) => col.sortOrder))
    picker.openForNewColumn(maxSortOrder + 1, { top: `${rect.bottom}px`, left: `${rect.left}px` })
  }

  return (
    <div
      id={`header-column-add`}
      data-tour="add-column"
      className={`absolute box-border inline-block bg-surface-sunken z-auto cursor-default border-r border-r-divider ${HEADER_HEIGHT_CLASS} ${COLUMN_MIN_WIDTH_CLASS}`}
      style={{
        left: `${totalWidth}px`
      }}
      onClick={handleAddColumn}
    >
      {/* Secondary to the columns: a muted + that darkens on hover, the size
          of the header's other icons. */}
      <Tooltip content="New column">
        <div
          role="button"
          tabIndex={0}
          aria-label="New column"
          data-testid="header-add-column-button"
          className="w-full h-full flex items-center justify-center absolute text-muted cursor-pointer select-none hover:bg-surface-strong hover:text-body transition-colors [&>svg]:w-3.5 [&>svg]:h-3.5"
          onKeyDown={(event: React.KeyboardEvent<HTMLDivElement>) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault()
              event.currentTarget.click()
            }
          }}
        >
          <Plus />
        </div>
      </Tooltip>

      <ColumnTypePickerMenu id="add-column-type-menu" picker={picker} zIndex={3000} />
    </div>
  )
}

export default HeaderAddColumn
